import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { ConfirmDialog, SelectOption, useNotify } from '@globaleris/ui.library'

import { getActivePrice, getVersions, notifyError } from '../../../../../redux'

import { PriceExpressionValidator } from '../../../../../components/shared'
import { EnumPriceCalculationType, EnumRedondeo } from '../../../../../api/Argentia/enums'
import { EnumPrecioUnitario } from '../../../../../api'
import { enumKeyToValue, enumValueToKey } from '../../../../../helpers'
import { useConfirmationDialog, useHttpError } from '../../../../../hooks'

import { styles } from './addVersionDialog.styles'
import { NewPriceVersion, PriceDetails } from '../../../pricing.Model'

import { checkGapOverlapVersions } from '../../utils/datesChecker'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { EnumPriceType } from '../../../../../api/oData/enums/enumPriceType'

export interface AddVersionDialogProps {
  show: boolean
  onRefreshPage: () => void
  onShowAddVersionDialog: (show: boolean) => void
  onAddVersion: (versionFields: Partial<NewPriceVersion>) => void
  newVersionNumber: number
  firstVersionStartDate: Date
  versions: any[]
}

export const AddVersionDialog: React.FC<AddVersionDialogProps> = ({
  show,
  onRefreshPage,
  onShowAddVersionDialog,
  onAddVersion,
  newVersionNumber,
  firstVersionStartDate,
  versions
}) => {
  const [t] = useTranslation([
    'priceExpressionValidator',
    'globalerisLibrary',
    'enums',
    'pricingDetails'
  ])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()

  const [versionFields, setVersionFields] = useState<Partial<NewPriceVersion>>({
    version: newVersionNumber
  })

  const [futurePrices, setFuturePrices] = useState<SelectOption[]>([])
  const [expressionIsValid, setExpressionIsValid] = useState<boolean | undefined>(undefined)
  const [selectedPriceCalculationType, setSelectedPriceCalculationType] = useState<
    EnumPriceCalculationType | undefined
  >(undefined)
  const [expression, setExpression] = useState<string | undefined>(undefined)
  const [priceUnit, setPriceUnit] = useState<EnumPrecioUnitario | undefined>(undefined)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [filledFields, setFilledFields] = useState<boolean>(false)

  const price = getActivePrice()
  // const versions = getVersions();
  const { showError, clearError, errorProps } = useHttpError()
  // const { getConfirmation, closeConfirmation } = useConfirmationDialog();

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [isWaitingConfirmation, setIsWaitingConfirmation] = useState<boolean>(false)
  const [confirmationConfig, setConfirmationConfig] = useState<any | undefined>(undefined)

  const onFormSaveHandler = async () => {
    if (!expressionIsValid) {
      notifyError(
        'No se ha podido guardar el precio. La expresión no es válida. Por favor, ingrese una expresión válida.'
      )
      return
    }

    const newVersion = {
      ...versionFields,
      expression: versionFields.expression,
      priceCalculationType: versionFields.priceCalculationType,
      redondeo: enumKeyToValue(EnumRedondeo, versionFields.redondeo),
      precioUnitario: enumKeyToValue(EnumPrecioUnitario, versionFields.precioUnitario)
    }

    if (versions) {
      const newVersionWithId = {
        ...newVersion,
        id: Math.random(),
        startDate: moment(newVersion.startDate).toDate().toString(),
        endDate: newVersion?.endDate
          ? moment(newVersion.endDate).toDate().toString()
          : newVersion.endDate
      }
      const versionsWithNewVersion = [...versions, newVersionWithId]
      const [isGap, isOverlap] = checkGapOverlapVersions(newVersionWithId, versionsWithNewVersion)
      if (isGap[0] && isOverlap[0]) {
        getConfirmation(ConfirmationDialogs.GAP_OVERLAP)
      } else if (isGap[0]) {
        const errorMsg = isGap[1]
        if (errorMsg === 'BOTH') {
          getConfirmation(ConfirmationDialogs.GAP_BOTH)
        } else if (errorMsg === 'PAST') {
          getConfirmation(ConfirmationDialogs.GAP_PAST)
        } else if (errorMsg === 'FUTURE') {
          getConfirmation(ConfirmationDialogs.GAP_FUTURE)
        }
      } else if (isOverlap[0]) {
        const errorMsg = isOverlap[1]
        if (errorMsg === 'BOTH') {
          getConfirmation(ConfirmationDialogs.OVERLAP_BOTH)
        } else if (errorMsg === 'PAST') {
          getConfirmation(ConfirmationDialogs.OVERLAP_PAST)
        } else if (errorMsg === 'FUTURE') {
          getConfirmation(ConfirmationDialogs.OVERLAP_FUTURE)
        }
      } else {
        onAddVersion(newVersion)
        setVersionFields({})
        onShowAddVersionDialog(false)
      }
    } else {
      onAddVersion(newVersion)
      setVersionFields({})
      onShowAddVersionDialog(false)
    }
  }

  const onCloseHandler = async (confirm: boolean): Promise<void> => {
    if (confirm && showConfirmation) {
      const newVersion = {
        ...versionFields,
        expression: versionFields.expression,
        priceCalculationType: versionFields.priceCalculationType,
        redondeo: enumKeyToValue(EnumRedondeo, versionFields.redondeo),
        precioUnitario: enumKeyToValue(EnumPrecioUnitario, versionFields.precioUnitario)
      }
      onAddVersion(newVersion)
      setShowConfirmation(false)
      info('Se ha creado la versión con éxito.')
      setVersionFields({})
      onShowAddVersionDialog(false)
    } else {
      setShowConfirmation(false)
    }
  }

  const getConfirmation = async (type: ConfirmationDialogs): Promise<void> => {
    const config = {
      title: t(`confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.title`),
      message: t(`confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.message`),
      okBtnLabel: t(`confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.actions.ok`),
      cancelBtnLabel: t(
        `confirmDialogs:${enumValueToKey(ConfirmationDialogs, type)}.actions.cancel`
      )
    }
    setConfirmationConfig(config)
    setShowConfirmation(true)
  }

  useEffect(() => {
    setVersionFields({ ...versionFields, version: newVersionNumber })
  }, [show, newVersionNumber])

  useEffect(() => {
    if (versionFields) {
      const isFormFilled =
        versionFields.expression &&
        versionFields.precioUnitario &&
        versionFields.priceCalculationType &&
        versionFields.startDate &&
        versionFields.version &&
        versionFields.redondeo
      isFormFilled && !filledFields && expressionIsValid && setFilledFields(true)
      !isFormFilled && filledFields && setFilledFields(false)
    }
  }, [versionFields, expressionIsValid])

  return (
    <Dialog onClose={() => {}} open={show} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.title}>
        {t('versions:components.addVersionDialog.title')}
      </DialogTitle>
      <DialogContent className={classes.formWithOutToolbar} dividers>
        {price ? (
          <PriceExpressionValidator
            showDates
            showPriceUnit
            showRounding
            showVersion
            show={true}
            priceType={enumKeyToValue(EnumPriceType, price.priceType)}
            commodity={price.commodity}
            onSelectPriceUnit={value => {
              setVersionFields({ ...versionFields, precioUnitario: value })
            }}
            onSelectPriceCalculationType={value => {
              setVersionFields({ ...versionFields, priceCalculationType: value })
            }}
            onSelectValidExpression={value => {
              setVersionFields({ ...versionFields, expression: value })
            }}
            onSelectRounding={value => {
              setVersionFields({ ...versionFields, redondeo: value })
            }}
            onSelectStartDate={value => {
              setVersionFields({
                ...versionFields,
                startDate: value ? moment(value).toDate() : undefined
              })
            }}
            onSelectEndDate={value => {
              setVersionFields({
                ...versionFields,
                endDate: value ? moment(value).toDate() : undefined
              })
            }}
            onIsValid={value => {
              setExpressionIsValid(value)
            }}
            // price={price}
            elevation={0}
            priceVersion={newVersionNumber}
            minVersionStartDate={firstVersionStartDate ? firstVersionStartDate : undefined}
            spacing={3}
            modal
          />
        ) : (
          <></>
        )}
        {confirmationConfig && (
          <ConfirmDialog
            open={showConfirmation}
            title={confirmationConfig.title}
            message={confirmationConfig.message}
            okBtnLabel={confirmationConfig.okBtnLabel}
            cancelBtnLabel={confirmationConfig.cancelBtnLabel}
            isWaiting={isWaitingConfirmation}
            onClose={onCloseHandler}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setVersionFields({})
            onShowAddVersionDialog(false)
          }}
        >
          {t('versions:components.addVersionDialog.mainActions.cancel')}
        </Button>
        <Button
          disabled={!filledFields}
          onClick={() => {
            onFormSaveHandler()
          }}
          color="primary"
        >
          {t('versions:components.addVersionDialog.mainActions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
